import React from "react";
import {
  Alert,
  Box,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import {
  EditorSection,
  EditorFormNav,
  EditorListItem,
  EditorListHeader,
  StringListEditor,
} from "./institutional/InstitutionalEditorParts";
import { emptyAgenciasPage } from "../../utils/agenciasPageContent";

const emptyAgency = () => ({
  id: "",
  city: "",
  state: "",
  address: "",
  phones: [],
  hours: [],
  mapsUrl: "",
  whatsappHrefs: [],
});

const emptyFaqItem = () => ({ question: "", answer: "" });

export default function AgenciasEditor({ value, onChange }) {
  const v = value?.format === "agencias.v1" ? value : emptyAgenciasPage();
  const set = (patch) => onChange({ ...v, ...patch });
  const setMeta = (patch) => set({ meta: { ...(v.meta || {}), ...patch } });
  const setHero = (patch) => set({ hero: { ...(v.hero || {}), ...patch } });
  const setFaq = (patch) => set({ faq: { ...(v.faq || {}), ...patch } });

  const agencies = Array.isArray(v.agencies) ? v.agencies : [];
  const faqItems = Array.isArray(v.faq?.items) ? v.faq.items : [];

  const setAgency = (i, patch) => {
    const next = [...agencies];
    next[i] = { ...next[i], ...patch };
    set({ agencies: next });
  };

  return (
    <Box sx={{ mt: 0.5 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Cadastre as agências rodoviárias. O site monta busca, filtros e mapas automaticamente —{" "}
        <strong>sem HTML</strong>.
      </Alert>

      <EditorFormNav
        sections={[
          { id: "sec-ag-google", label: "Google" },
          { id: "sec-ag-abertura", label: "Abertura" },
          { id: "sec-ag-faq", label: `FAQ (${faqItems.length})` },
          { id: "sec-ag-lista", label: `Agências (${agencies.length})` },
        ]}
      />

      <EditorSection id="sec-ag-google" title="Como a página aparece no Google" optional defaultExpanded={false}>
        <TextField
          fullWidth
          label="Título da página"
          value={v.meta?.title || ""}
          onChange={(e) => setMeta({ title: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Descrição curta"
          value={v.meta?.description || ""}
          onChange={(e) => setMeta({ description: e.target.value })}
        />
      </EditorSection>

      <EditorSection id="sec-ag-abertura" title="Abertura da página" subtitle="Texto no topo antes da lista de agências" defaultExpanded>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Etiqueta superior"
              value={v.hero?.eyebrow || ""}
              onChange={(e) => setHero({ eyebrow: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Título principal"
              value={v.hero?.title || ""}
              onChange={(e) => setHero({ title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Texto de apoio"
              value={v.hero?.subtitle || ""}
              onChange={(e) => setHero({ subtitle: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Rótulo da busca (acessibilidade)"
              value={v.hero?.searchLabel || ""}
              onChange={(e) => setHero({ searchLabel: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Placeholder da busca"
              value={v.hero?.searchPlaceholder || ""}
              onChange={(e) => setHero({ searchPlaceholder: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Rótulo do filtro de UF"
              value={v.hero?.ufLabel || ""}
              onChange={(e) => setHero({ ufLabel: e.target.value })}
            />
          </Grid>
        </Grid>
      </EditorSection>

      <EditorSection
        id="sec-ag-faq"
        title="Perguntas frequentes"
        subtitle="Opcional — exibidas no rodapé da página"
        badge={faqItems.length || null}
        optional
        defaultExpanded={faqItems.length > 0}
      >
        <TextField
          fullWidth
          label="Título da seção FAQ"
          value={v.faq?.title || ""}
          onChange={(e) => setFaq({ title: e.target.value })}
          sx={{ mb: 2 }}
        />
        <EditorListHeader
          onAdd={() => setFaq({ items: [...faqItems, emptyFaqItem()] })}
          addLabel="+ Adicionar pergunta"
        />
        {faqItems.map((item, i) => (
          <EditorListItem
            key={i}
            index={i}
            title={`Pergunta ${i + 1}`}
            summary={item.question}
            defaultExpanded={i === faqItems.length - 1}
            onDelete={() => setFaq({ items: faqItems.filter((_, idx) => idx !== i) })}
          >
            <TextField
              fullWidth
              size="small"
              label="Pergunta"
              value={item.question || ""}
              onChange={(e) => {
                const items = [...faqItems];
                items[i] = { ...items[i], question: e.target.value };
                setFaq({ items });
              }}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Resposta"
              value={item.answer || ""}
              onChange={(e) => {
                const items = [...faqItems];
                items[i] = { ...items[i], answer: e.target.value };
                setFaq({ items });
              }}
            />
          </EditorListItem>
        ))}
      </EditorSection>

      <EditorSection
        id="sec-ag-lista"
        title="Lista de agências"
        subtitle="Endereço, telefones e horários de cada ponto de venda"
        badge={agencies.length || null}
        defaultExpanded
      >
        <EditorListHeader
          onAdd={() => set({ agencies: [...agencies, emptyAgency()] })}
          addLabel="+ Adicionar agência"
        />
        {agencies.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma agência cadastrada.
          </Typography>
        ) : null}
        {agencies.map((agency, i) => (
          <EditorListItem
            key={i}
            index={i}
            title={agency.city ? `${agency.city}${agency.state ? ` — ${agency.state}` : ""}` : `Agência ${i + 1}`}
            summary={agency.address}
            defaultExpanded={i === agencies.length - 1}
            onDelete={() => set({ agencies: agencies.filter((_, idx) => idx !== i) })}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Cidade *"
                  value={agency.city || ""}
                  onChange={(e) => setAgency(i, { city: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="UF"
                  value={agency.state || ""}
                  onChange={(e) => setAgency(i, { state: e.target.value.toUpperCase() })}
                  inputProps={{ maxLength: 2 }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="ID (âncora)"
                  value={agency.id || ""}
                  onChange={(e) => setAgency(i, { id: e.target.value })}
                  placeholder="auto se vazio"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Endereço"
                  value={agency.address || ""}
                  onChange={(e) => setAgency(i, { address: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Link do mapa (mapsUrl)"
                  value={agency.mapsUrl || ""}
                  onChange={(e) => setAgency(i, { mapsUrl: e.target.value })}
                />
              </Grid>
            </Grid>
            <StringListEditor
              label="Telefones"
              items={agency.phones}
              onChange={(phones) => setAgency(i, { phones })}
              placeholder="(14) 3496-2541"
            />
            <StringListEditor
              label="Horários de atendimento"
              items={agency.hours}
              onChange={(hours) => setAgency(i, { hours })}
              placeholder="Seg 08:00 - 18:00"
            />
            <StringListEditor
              label="Links WhatsApp"
              items={agency.whatsappHrefs}
              onChange={(whatsappHrefs) => setAgency(i, { whatsappHrefs })}
              placeholder="https://wa.me/5514999999999"
            />
          </EditorListItem>
        ))}
      </EditorSection>
    </Box>
  );
}
